/**
 * Career story logs (M7 spec §12.1 "logging now": history can't be backfilled). Two bounded, append-only logs on the
 * Career, both optional members (absent = empty, so older saves load unchanged):
 *
 * - `guardianKills`: `{ day, systemId, shipId }` per guardian ship the player destroyed (the worker's faction day;
 *   fed by `AdvanceResult.guardianKills` through the Charter career bridge), plus the M7 fields when the worker sends
 *   them (M7 S1): `hull`, `pilotSeed` and `selfDefence` (only when true).
 * - `tetherSales`: `{ day, stationId, tonnes, credits }` per tetherstone sale (`economy/trade.ts` `sellWare`; the day
 *   is the Career clock's, `gameTime ÷ saleDayS`).
 *
 * Each keeps its last N entries (`career-logs.json`); the oldest drops first. Appends are commands, not a hot path.
 */
import { z } from 'zod'
import logsJson from '../data/career-logs.json'
import type { Career, GuardianKillEntry, TetherSaleEntry } from './types'

export const careerLogsSchema = z.strictObject({
  comment: z.string(),
  guardianKillsMax: z.number().int().positive(),
  tetherSalesMax: z.number().int().positive(),
  saleDayS: z.number().positive(),
})
export type CareerLogsConfig = z.infer<typeof careerLogsSchema>

export const CAREER_LOGS: CareerLogsConfig = careerLogsSchema.parse(logsJson)

/** `log` plus `entry`, keeping the last `max` entries. */
function appendBounded<T>(log: readonly T[] | undefined, entry: T, max: number): T[] {
  const prev = log ?? []
  const keep = prev.length >= max ? prev.slice(prev.length - max + 1) : prev
  return [...keep, entry]
}

/** The Career's guardian kill log (empty when absent). */
export function guardianKillsOf(career: Career): readonly GuardianKillEntry[] {
  return career.guardianKills ?? []
}

/** The Career's tetherstone sale log (empty when absent). */
export function tetherSalesOf(career: Career): readonly TetherSaleEntry[] {
  return career.tetherSales ?? []
}

/** A kill entry with only the known fields (absent M7 fields stay absent, so the save stays compact). */
function killEntry(k: GuardianKillEntry): GuardianKillEntry {
  const e: GuardianKillEntry = { day: k.day, systemId: k.systemId, shipId: k.shipId }
  if (k.hull !== undefined) e.hull = k.hull
  if (k.pilotSeed !== undefined) e.pilotSeed = k.pilotSeed
  if (k.selfDefence === true) e.selfDefence = true
  return e
}

/** The Career with `kills` appended to its guardian kill log (bounded by `max`). */
export function withGuardianKills(
  career: Career,
  kills: readonly GuardianKillEntry[],
  max: number = CAREER_LOGS.guardianKillsMax,
): Career {
  if (kills.length === 0) return career
  let log: GuardianKillEntry[] | undefined = career.guardianKills
  for (const k of kills) log = appendBounded(log, killEntry(k), max)
  return { ...career, guardianKills: log }
}

/** The Career clock's day for sale logs (`gameTime ÷ saleDayS`, whole days). */
export function careerDay(career: Career, dayS: number = CAREER_LOGS.saleDayS): number {
  return Math.floor(career.gameTime / dayS)
}

/** The Career with one tetherstone sale appended (bounded by `max`). */
export function withTetherSale(career: Career, sale: TetherSaleEntry, max: number = CAREER_LOGS.tetherSalesMax): Career {
  return { ...career, tetherSales: appendBounded(career.tetherSales, sale, max) }
}
